import styled from 'styled-components';

import { ANIM, COLORS, DESKTOP, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

/** Mirrors the Settings forms (Billing / Profile): a single column with 24px between groups. */
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[24]};
`;

export const Intro = styled(Text)`
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const GroupTitle = styled(Text)`
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

/** Two-up field grid on desktop, single column on mobile (as the Billing form). */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACE[16]};

  ${DESKTOP} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** A cell spanning the full grid width (subject / message). */
export const FullCell = styled(FieldCell)`
  ${DESKTOP} {
    grid-column: 1 / -1;
  }
`;

export const FieldError = styled(Text)`
  font-size: 13px;
  color: ${COLORS.statusDanger};
`;

export const Divider = styled.hr`
  height: 1px;
  margin: 0;
  border: none;
  background: ${COLORS.settingsDivider};
`;

/* ── Page-local multi-line input ─────────────────────────────────────────────
 * The shared `Field` is single-line; a contact message needs a textarea. Styled
 * to match `Field` (same label, 12px radius, inset surface, panel border) so it
 * sits seamlessly beside the reused inputs, and themes via the same tokens. */

export const MsgRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const MsgLabel = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const MsgBox = styled.div<{ $invalid: boolean }>`
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.panelBorder)};
  background: ${COLORS.surfaceInset};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The message drops the browser's outline, so its box says it has the focus. An invalid one stays red. */
  &:focus-within {
    border-color: ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.accent)};
  }
`;

export const MsgInput = styled.textarea`
  display: block;
  width: 100%;
  min-height: 140px;
  resize: vertical;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;
  line-height: 1.5;
  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/** Remaining-characters hint, right-aligned and muted. */
export const CharCount = styled(Text)`
  align-self: flex-end;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const SendError = styled(Text)`
  font-size: 13px;
  color: ${COLORS.statusDanger};
`;

/**
 * Submit action — full-width on mobile; from desktop up a hairline separates it
 * from the fields and the button shrinks to half-width, centred (the `&&` doubles
 * specificity to beat the Button's own `width: 100%`). Mirrors the Settings forms.
 */
export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};

  ${DESKTOP} {
    align-items: center;
    border-top: 1px solid ${COLORS.settingsDivider};
    padding-top: ${SPACE[20]};

    && > * {
      width: 50%;
    }
  }
`;
